import { encodeReferralHash } from './referral-hash';
import { sanitizeRedirectPath } from './safe-redirect';
import { REFERRAL_POINTS } from './referral';

/**
 * THE REFERRAL SHARE KIT — one module every invite surface builds from, in BOTH apps.
 *
 * The proven give/get share shape (Wise/Monzo/PayPal, and every trending Mobbin
 * "Invite & Refer Friends" screen): a pre-written personal message with the link
 * embedded, the native share sheet as the mobile primary, explicit channel deep links
 * as the fallback, copy always available. Channel mix for referral shares is roughly
 * SMS 45% · email 25% · social DM 15% · copy-link 10%, so the channel buttons lead
 * with SMS.
 *
 * The link is the member's ONE referral link, built exactly the way the /points page
 * builds it: `encodeReferralHash(asfId)` over Clerk publicMetadata, zero GraphQL.
 *
 * Framework-free `.ts` so town-square (Next) and client (Vite) share ONE copy. The
 * town-square base URL is the one thing that differs per app, so the link builders take
 * it as an argument (`NEXT_PUBLIC_TOWN_SQUARE_URL` in Next, `__TOWN_SQUARE_URL__` in client).
 */

/**
 * @param baseUrl The town-square origin the invite links point at (the app's own
 *   town-square URL). Invite/referral links always land on town-square regardless of which
 *   app the modal is shown in.
 * @param destPath Optional town-square-relative destination (e.g. `/quests/sell?join=1`).
 *   Carried as Clerk's own `redirect_url` param — Clerk honors it over its fallback and
 *   preserves it through the verify steps — so the friend lands THERE after signing up,
 *   not on the generic post-signup page. Sanitized to a same-origin relative path; an
 *   invalid destination is dropped, never trusted.
 */
export function buildReferralLink(
  asfIdRaw: string | undefined,
  baseUrl: string,
  destPath?: string | null
): string | null {
  const asfId = asfIdRaw ? Number(asfIdRaw) : NaN;
  if (!Number.isInteger(asfId) || asfId === 0) return null;
  const base = baseUrl;
  const safeDest = destPath ? sanitizeRedirectPath(destPath, undefined) : undefined;
  if (safeDest) {
    return `${base}/signup?r=${encodeReferralHash(asfId)}&redirect_url=${encodeURIComponent(`${base}${safeDest}`)}`;
  }
  // The generic invite lands on the FRIEND landing — the inviter is the headline
  // (Nick, 2026-09-01). Legacy /signup?r= links in the wild keep working.
  return `${base}/invite?invite=${encodeReferralHash(asfId)}`;
}

/**
 * THE PROMO CONTEXT — the cross-app abstraction (Nick, 2026-09-01: "we are going to
 * use this across this app and in our satellite apps"). An invite always promotes
 * SOMETHING — a quest here, an art review in the art-review app — and that something
 * supplies three facts: what kind of thing it is, its name (woven into the message),
 * and the PUBLIC landing that sells it. Everything else — the modal, the share kit,
 * the `?invite=<hash>` referral param, the embedded-signup landing, the server-side
 * once-ever fulfillment — is generic and reads only this shape.
 *
 * Cross-app canon: agent-standards/docs/referral-invite-pattern.md.
 */
export type InvitePromo = {
  /** What is being promoted — 'quest' here; each app registers its own kinds. */
  kind: string;
  /** Stable identifier of the promoted thing (rides signup metadata for fulfillment). */
  slug: string;
  /** Human name, quoted into the invite message. */
  name: string;
  /** App-relative path of the public landing that promotes it. */
  landingPath: string;
};

/** The quest kind's promo. Satellite apps define their own factory like this one. */
export function questPromo(slug: string, name: string): InvitePromo {
  return { kind: 'quest', slug, name, landingPath: `/quests/${slug}` };
}

/**
 * A guild invitation. The link lands on the guild's own page so the invitee sees the work
 * before signing up — the headline case in the guild issue.
 *
 * The hash this produces is ATTRIBUTION ONLY. It is unsalted Sqids over asfId and is
 * forgeable, so it can credit a referrer but must never grant membership; acceptance
 * rides the directed token on the guild_invites row.
 */
export function guildPromo(slug: string, name: string): InvitePromo {
  return { kind: 'guild', slug, name, landingPath: `/guilds/${slug}` };
}

/**
 * The invite link for a promo: its PUBLIC landing with `?invite=<hash>` — the thing
 * sells itself with signup embedded in place. Without a promo: the plain /signup link.
 */
export function buildInviteLinkFor(
  asfIdRaw: string | undefined,
  baseUrl: string,
  promo?: InvitePromo | null
): string | null {
  if (!promo) return buildReferralLink(asfIdRaw, baseUrl);
  const asfId = asfIdRaw ? Number(asfIdRaw) : NaN;
  if (!Number.isInteger(asfId) || asfId === 0) return null;
  const base = baseUrl;
  const safePath = sanitizeRedirectPath(promo.landingPath, undefined);
  if (!safePath) return buildReferralLink(asfIdRaw, baseUrl);
  const joiner = safePath.includes('?') ? '&' : '?';
  return `${base}${safePath}${joiner}invite=${encodeReferralHash(asfId)}`;
}

/**
 * The invite message — first person, short enough to read whole in an SMS preview. It
 * deliberately does NOT name the gift: the mystery plus the 24-hour line is the reason to tap
 * now (Nick, 2026-10-15). Same wording as the gift-invite email subject.
 */
export function buildInviteMessage(link: string): string {
  return `I sent you a gift - expires in 24 hours: ${link}`;
}

/** The quest-complete brag — the share IS a referral. `points` is the live referral amount. */
export function buildCompletionMessage(questName: string, link: string, points: number = REFERRAL_POINTS): string {
  return `I just completed the ${questName} quest on ArtHelper 🎉 Join me and you'll get ${points} points: ${link}`;
}

export function canShareNative(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Open the native share sheet. Resolves true when the sheet was handed the content,
 * false when unsupported or the member dismissed it (AbortError) — dismissal is a
 * normal outcome, never an error.
 */
export async function shareNative(message: string, url: string): Promise<boolean> {
  if (!canShareNative()) return false;
  try {
    await navigator.share({ text: message, url });
    return true;
  } catch {
    return false;
  }
}

/** Channel deep links, message prefilled. The `?&body` form works on iOS and Android. */
export function smsHref(message: string): string {
  return `sms:?&body=${encodeURIComponent(message)}`;
}

export function whatsappHref(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function emailHref(subject: string, message: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
}

/** X/Twitter compose with the full message (the link rides inside it). */
export function xHref(message: string): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
}

/** Facebook's sharer takes a URL; the page's own OG data does the talking. */
export function facebookHref(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}
